import React, { useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from './visuals/motion';

/* Ported from sites/viridia-site-rebuild/reference/yelp-report-mock.html
   (Kevin, 2026-09-15) -- same numbers, same labels, same count-up on
   scroll, same "Example data" tag. Vanilla React state + rAF instead of
   the reference's plain DOM script, no charting library. Reduced-motion
   safe: values render at their final number immediately instead of
   counting up. */

type Kpi = {
  label: string;
  value: number;
  prefix?: string;
  suffix?: string;
  note: string;
};
const kpis: Kpi[] = [{
  label: 'Ad spend',
  value: 3000,
  prefix: '$',
  note: 'at Yelp’s exact charge'
}, {
  label: 'Clicks',
  value: 412,
  note: '$7.28 per click'
}, {
  label: 'Calls answered',
  value: 61,
  note: 'of 64 calls · 3 missed, all texted back'
}, {
  label: 'Quote requests answered',
  value: 38,
  note: 'of 38 · avg reply 4 min'
}, {
  label: 'Estimates booked',
  value: 19,
  note: 'from calls and quote requests'
}, {
  label: 'Response time',
  value: 4,
  suffix: ' min',
  note: 'Yelp average on this page before us: 3 hours'
}];
function useCountUp(target: number, active: boolean, reduced: boolean) {
  const [value, setValue] = useState(reduced ? target : 0);
  useEffect(() => {
    if (!active) return;
    if (reduced) {
      setValue(target);
      return;
    }
    let raf: number;
    const start = performance.now();
    const dur = 900;
    const step = (t: number) => {
      const k = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - k, 3);
      setValue(target * eased);
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [active, reduced, target]);
  return value;
}
function fmt(n: number, prefix = '', suffix = '') {
  return `${prefix}${Math.round(n).toLocaleString('en-US')}${suffix}`;
}
function KpiValue({
  kpi,
  active,
  reduced,
  accent
}: {
  kpi: Kpi;
  active: boolean;
  reduced: boolean;
  accent?: boolean;
}) {
  const value = useCountUp(kpi.value, active, reduced);
  return <div className={`font-mono text-3xl font-semibold tabular-nums ${accent ? 'text-emerald-500' : 'text-white'}`} data-id="element-866" data-mp="2cjp1xsznawem">
      {fmt(value, kpi.prefix, kpi.suffix)}
    </div>;
}
export function YelpReportExample() {
  const reduced = usePrefersReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);
  useEffect(() => {
    if (!ref.current || active) return;
    const io = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        setActive(true);
        io.disconnect();
      }
    }, {
      threshold: 0.3
    });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [active]);
  const costPerJob: Kpi = {
    label: 'Cost per booked job',
    value: 158,
    prefix: '$',
    note: 'The number Yelp’s dashboard never shows you. Spend divided by jobs that actually got booked, not clicks.'
  };
  const weeks = [3, 4, 5, 7];
  return <div ref={ref} className="mx-auto w-full max-w-3xl overflow-hidden rounded-md border border-[#22302a] bg-[#111815]" data-id="element-867" data-mp="3hkf0y4kgiqss">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#22302a] px-5 py-4" data-id="element-868" data-mp="3lo39apgwxqf3">
        <div data-id="element-869" data-mp="37ova3wkr2iaw">
          <h3 className="text-base font-extrabold text-[#e9efeb]" data-id="element-870" data-mp="37p3ojswitvo7">Yelp Ads report &middot; August</h3>
          <p className="text-sm text-[#97a69e]" data-id="element-871" data-mp="3cnomfloaw39h">Phoenix plumbing company &middot; one Yelp page &middot; budget $3,000</p>
        </div>
        <span className="rounded border border-[#f0c060] px-2 py-1 font-mono text-[11px] uppercase tracking-[0.12em] text-[#f0c060]" data-id="element-872" data-mp="95enaflyrkol">
          Example data
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3" data-id="element-873" data-mp="3vk6czzrexx45">
        <div className="col-span-full flex flex-wrap items-end justify-between gap-4 border-b border-[#22302a] bg-gradient-to-b from-emerald-500/10 to-emerald-500/[0.02] px-5 py-5" data-id="element-874" data-mp="3viv6hs6l2b9c">
          <div data-id="element-875" data-mp="rx7fi7t7l1v9">
            <p className="text-xs font-bold uppercase tracking-[0.08em] text-[#97a69e]" data-id="element-876" data-mp="rxpjxrq0yme9">{costPerJob.label}</p>
            <div className="mt-1 font-mono text-[44px] font-semibold text-emerald-500 tabular-nums" data-id="element-877" data-mp="2zcuwsubehgub">
              <KpiValue kpi={costPerJob} active={active} reduced={reduced} accent data-id="element-878" data-mp="2vfytkr06p1nf" />
            </div>
          </div>
          <p className="max-w-[36ch] text-[13.5px] text-[#97a69e]" data-id="element-879" data-mp="2lfofz5a8ls7q">{costPerJob.note}</p>
        </div>

        {kpis.map((kpi, i) => <div key={kpi.label} className={`border-b border-[#22302a] px-5 py-4.5 sm:px-5 sm:py-[18px] ${(i + 1) % 3 !== 0 ? 'sm:border-r sm:border-[#22302a]' : ''}`} data-id="element-880" data-mp="3j25eueymcdvl" data-mpi={i}>
            <p className="text-xs font-bold uppercase tracking-[0.08em] text-[#97a69e]" data-id="element-881" data-mp="39baoozi94u4v">{kpi.label}</p>
            <KpiValue kpi={kpi} active={active} reduced={reduced} accent={kpi.label === 'Estimates booked'} data-id="element-882" data-mp="34c6f47rf0ue0" />
            <p className="mt-0.5 text-[12.5px] text-[#97a69e]" data-id="element-883" data-mp="5q3n2xt7ntcn">{kpi.note}</p>
          </div>)}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1.2fr_1fr]" data-id="element-884" data-mp="88ykwz0wpzyx">
        <div className="border-b border-[#22302a] px-5 pb-3 pt-4 sm:border-b-0 sm:border-r" data-id="element-885" data-mp="8ajiyymfk11j">
          <h4 className="mb-2 text-xs font-bold uppercase tracking-[0.08em] text-[#97a69e]" data-id="element-886" data-mp="d81sor89ukal">Jobs booked, by week</h4>
          <svg viewBox="0 0 360 150" role="img" aria-label="Jobs booked per week: 3, 4, 5, 7" className="block w-full" data-id="element-887" data-mp="i98zamyohdfo">
            <g stroke="#1a2420" data-id="element-888" data-mp="r7ud64t7g4a4">
              <line x1="30" y1="120" x2="350" y2="120" data-id="element-889" data-mp="r6j7u0k42ogw" />
              <line x1="30" y1="80" x2="350" y2="80" data-id="element-890" data-mp="w6nsm2s1y5cx" />
              <line x1="30" y1="40" x2="350" y2="40" data-id="element-891" data-mp="w52xoc1tskej" />
            </g>
            <g fill="#97a69e" fontFamily="JetBrains Mono, monospace" fontSize="11" textAnchor="end" data-id="element-892" data-mp="10x459ko1tmy8">
              <text x="24" y="124" data-id="element-893" data-mp="15wqkfznftd9o">0</text>
              <text x="24" y="84" data-id="element-894" data-mp="15yjs9i9se3j9">4</text>
              <text x="24" y="44" data-id="element-895" data-mp="1aw3hfd80w0pz">8</text>
            </g>
            {weeks.map((w, i) => {
            const x = 60 + i * 70;
            const h = w * 10;
            const y = 120 - h;
            return <rect key={i} x={x} y={active || reduced ? y : 120} width="48" height={active || reduced ? h : 0} fill="#2fd48a" style={{
              transition: reduced ? undefined : `y 0.7s cubic-bezier(.2,.7,.2,1) ${0.08 * i}s, height 0.7s cubic-bezier(.2,.7,.2,1) ${0.08 * i}s`
            }} data-id="element-896" data-mp="3byum32aynx8t" data-mpi={i} />;
          })}
            <g fill="#e9efeb" fontFamily="JetBrains Mono, monospace" fontSize="12" fontWeight={600} textAnchor="middle" data-id="element-897" data-mp="3poy0x87igoo8">
              {weeks.map((w, i) => <text key={i} x={84 + i * 70} y={120 - w * 10 - 6} data-id="element-898" data-mp="3hpzui3mj3y0" data-mpi={i}>
                  {w}
                </text>)}
            </g>
            <g fill="#97a69e" fontFamily="JetBrains Mono, monospace" fontSize="11" textAnchor="middle" data-id="element-899" data-mp="29b7sjp9cze8s">
              {['wk 1', 'wk 2', 'wk 3', 'wk 4'].map((label, i) => <text key={label} x={84 + i * 70} y="140" data-id="element-900" data-mp="1v6pbsoxyx7qr" data-mpi={i}>
                  {label}
                </text>)}
            </g>
          </svg>
        </div>

        <div className="px-5 pb-3 pt-4" data-id="element-901" data-mp="1lg4hhdpbupdn">
          <h4 className="mb-2 text-xs font-bold uppercase tracking-[0.08em] text-[#97a69e]" data-id="element-902" data-mp="1lf33p1gxfos8">Reply time on quote requests</h4>
          <svg viewBox="0 0 300 150" role="img" aria-label="Average reply time fell from 3 hours to 4 minutes across the month" className="block w-full" data-id="element-903" data-mp="1qccqsagufocw">
            <g stroke="#1a2420" data-id="element-904" data-mp="1ghkxhiuannnx">
              <line x1="30" y1="120" x2="290" y2="120" data-id="element-905" data-mp="1gef3bp6t5vlc" />
              <line x1="30" y1="40" x2="290" y2="40" data-id="element-906" data-mp="12hs7xqui7dhj" />
            </g>
            <g fill="#97a69e" fontFamily="JetBrains Mono, monospace" fontSize="11" textAnchor="end" data-id="element-907" data-mp="17fjxie7oenik">
              <text x="24" y="44" data-id="element-908" data-mp="2wv19mwgt5up8">3h</text>
              <text x="24" y="124" data-id="element-909" data-mp="2ww2t9pbw3pik">0</text>
            </g>
            <path d="M40 44 L110 100 L180 114 L270 116" fill="none" stroke="#2fd48a" strokeWidth="2.5" strokeDasharray="600" strokeDashoffset={active || reduced ? 0 : 600} style={{
            transition: reduced ? undefined : 'stroke-dashoffset 1.2s ease-out 0.2s'
          }} data-id="element-910" data-mp="31tkz36um1omc" />
            <circle cx="270" cy="116" r="5" fill="#2fd48a" opacity={active || reduced ? 1 : 0} style={{
            transition: reduced ? undefined : 'opacity 0.3s ease-out 1.3s'
          }} data-id="element-911" data-mp="4h003q0wsl8n" />
            <text x="262" y="104" fill="#2fd48a" fontFamily="JetBrains Mono, monospace" fontSize="12" fontWeight={600} textAnchor="end" data-id="element-912" data-mp="3mnxuf196jbiy">
              4 min
            </text>
            <g fill="#97a69e" fontFamily="JetBrains Mono, monospace" fontSize="11" textAnchor="middle" data-id="element-913" data-mp="3dosw9w1j5k7b">
              <text x="40" y="140" data-id="element-914" data-mp="1fx83fto600ld">before</text>
              <text x="270" y="140" data-id="element-915" data-mp="1fw5a7f3ddm2a">wk 4</text>
            </g>
          </svg>
        </div>
      </div>

      <div className="flex flex-wrap justify-between gap-3 border-t border-[#22302a] px-5 py-3 text-[12.5px] text-[#97a69e]" data-id="element-916" data-mp="1butldbsbl4v5">
        <p data-id="element-917" data-mp="2ysueoce7dyhh">Pulled from Yelp&rsquo;s advertising API, joined to what happened after the click.</p>
        <p data-id="element-918" data-mp="33kccwsoudkyr">Example report. Your numbers will be yours.</p>
      </div>
    </div>;
}